import React, { Suspense } from 'react';

import { matchRoutes, useLocation } from 'react-router-dom';

import { Box, Center, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import ErrorBoundary from 'components/common/ErrorBoundary';
import InternalToolbar from 'components/internalToolbar';
import OnboardingHeader from 'components/layout/LayoutHeader/OnboardingHeader';
import loadable from 'helpers/lazyWithPreload';
import { useOnboardingTasks } from 'queries/onboardingTasks';

import Footer from './Footer';

const NavigationV2 = loadable(() => import('components/layout/NavigationV2'));

const SPINNER_TIMEOUT = 300;

const ContentLoader = () => {
  const [showSpinner, setShowSpinner] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(true);
    }, SPINNER_TIMEOUT);

    return () => clearTimeout(timer);
  }, []);

  // Return empty box with same height to prevent layout shift
  if (!showSpinner) {
    return <Box h="400px" w="100%" />;
  }

  return (
    <Center h="400px" w="100%">
      <VStack spacing={4}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
        <Text color="gray.600" fontSize="sm">
          Loading...
        </Text>
      </VStack>
    </Center>
  );
};

const FullPageLayout = ({ children }) => {
  const location = useLocation();
  const { data: onboardingTasksData } = useOnboardingTasks();
  const onboardingComplete = onboardingTasksData?.metadata?.onboarding_complete;
  const noFooterRoutes = matchRoutes(
    [{ path: '/org/:id/incidents/:id/*' }, { path: '/incidents/:id/*' }],
    location,
  );

  // Base routes that should never show navigation
  const excludeNavRoutes = [
    { path: '/' },
    { path: '/registrations/new' },
    { path: '/sessions/new' },
    { path: '/sessions/sso/new' },
  ];

  // Integration onboarding routes with /settings/ included in the paths
  if (!onboardingComplete) {
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/zoom/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/webex/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/google_calendar/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/jira_cloud/site_selection/:id',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/jira_cloud/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/jira_cloud/connections/:connectionId/projects/:projectId/:configId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/service_now/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/service_now/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/service_now/connections/:connectionId/projects/:projectId/:configId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/slack/connections/:connectionId/onboarding',
    });
  }

  // Check if current location matches any of the exclude routes
  const noNavRoutes = matchRoutes(excludeNavRoutes, location);

  let boxProps = {
    ml: 6,
    mr: 4,
    w: '100%',
    maxW: '1800px',
    px: ['2', '2', '3', '4'],
    py: 3,
    m: 0,
  };

  if (noFooterRoutes) {
    boxProps.pb = 0;
  }

  return (
    <>
      <InternalToolbar />
      <Flex minHeight="100vh" width="100%" flexDirection={'column'}>
        {!noNavRoutes && (
          <Suspense
            fallback={
              <Box h="64px" bg="gray.100" /> // Placeholder for nav height
            }
          >
            <NavigationV2 />
          </Suspense>
        )}

        <OnboardingHeader pathname={location.pathname} />

        <Flex
          flex="1"
          direction="column"
          justifyContent="space-between"
          alignItems={'center'}
        >
          <Box flex="1" {...boxProps}>
            <ErrorBoundary>
              {/* THIS IS THE KEY SUSPENSE BOUNDARY */}
              {/* Only the content suspends, layout stays stable */}
              <Suspense fallback={<ContentLoader />}>{children}</Suspense>
            </ErrorBoundary>
          </Box>
          {!noFooterRoutes && (
            <>
              <Flex w="100%" justifyContent="flex-end">
                <Footer />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </>
  );
};

FullPageLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FullPageLayout;
